import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./OverallStatus.module.css";
import { getAllDevices, getPendingCount } from "../../api/api";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRefresh,
  faDownload
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

export default function OverallStatus({
  onSelectDevice,
  businessId,
  businessName,
  schedule
}) {
  const [pendingCount, setPendingCount] = useState("");
  const [filterText, setFilterText] = useState("");
  const [activeChecked, setActiveChecked] = React.useState(localStorage.getItem('allDevicesActiveChecked') !== 'N');
  const [inactiveChecked, setInactiveChecked] = React.useState(localStorage.getItem('allDevicesInactiveChecked') !== 'N');
  const [neverActiveChecked, setNeverActiveChecked] = React.useState(localStorage.getItem('allDevicesNeverActiveChecked') !== 'N');

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllDevices(businessId);
      let sortedData = data.data.sort(deviceSorter);
      setDevices(sortedData);
      const pendingCount = await getPendingCount(businessId);
      setPendingCount(pendingCount.pending_count);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching device list:", error);
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [businessId, fetchData]);

  useEffect(() => {
    if (!loading && schedule) {
      const element = document.querySelector(`tr[data-schedule-id="${schedule.id}"]`);
      if (element) {
        element.scrollIntoView({ block: 'center' });
      }
    }
  }, [loading, schedule]);

  const searchBoxChanged = (newValue) => {
    setFilterText(newValue);
    return true;
  }

  function activecheckBoxChanged(e) {
    setActiveChecked(e.target.checked);
    localStorage.setItem('allDevicesActiveChecked', e.target.checked ? 'Y' : 'N')
  }

  function inactivecheckBoxChanged(e) {
    setInactiveChecked(e.target.checked);
    localStorage.setItem('allDevicesInactiveChecked', e.target.checked ? 'Y' : 'N')
  }

  function neverActivecheckBoxChanged(e) {
    setNeverActiveChecked(e.target.checked);
    localStorage.setItem('allDevicesNeverActiveChecked', e.target.checked ? 'Y' : 'N')
  }

  // Use useMemo to calculate filtered values and counts only when dependencies change
  const { filteredDevices, counts } = useMemo(() => {
    let filteredDevices = [...devices];
    let activeCounter = 0;
    let inactiveCounter = 0;
    let neverActiveCounter = 0;
    
    // Apply text filter
    if (filterText !== "") {
      const regex = new RegExp(`.*${filterText}.*`, "i");
      filteredDevices = filteredDevices.filter(s => regex.test(s.schedule_name));
    }
    
    // Count all statuses first
    filteredDevices.forEach(device => {
      if (device.status === "ACTIVE") activeCounter++;
      else if (device.status === "INACTIVE") inactiveCounter++;
      else if (device.status === "N/A") neverActiveCounter++;
    });
    
    // Then filter based on checkbox selections
    filteredDevices = filteredDevices.filter(device => {
      if (device.status === "ACTIVE" && !activeChecked) return false;
      if (device.status === "INACTIVE" && !inactiveChecked) return false;
      if (device.status === "N/A" && !neverActiveChecked) return false;
      return true;
    });
    
    return { 
      filteredDevices, 
      counts: {
        active: activeCounter,
        inactive: inactiveCounter,
        neverActive: neverActiveCounter
      }
    };
  }, [devices, filterText, activeChecked, inactiveChecked, neverActiveChecked]);

  function deviceSorter(a, b) {
    if (a.schedule_name < b.schedule_name) {
      return -1;
    } else if (a.schedule_name > b.schedule_name) {
      return 1;
    } else {
      if (a.serial_no < b.serial_no) {
        return -1;
      } else if (a.serial_no > b.serial_no) {
        return 1;
      } else {
        return 0;
      }
    }
  }
  
  const getStatusColor = (status) => {
    return status === "ACTIVE" ? styles.active : styles.inactive;
  };

  const handleRowClick = (deviceId, scheduleId) => {
    onSelectDevice(deviceId, scheduleId);
  };
  
  const convertToCSV = (data, headers, columns) => {
    if (!data || data.length === 0) {
      return '';
    }
    
    // Create header row
    const csvRows = [headers.join(',')];
    
    // Add data rows
    for (const row of data) {
      const values = columns.map(column => {
        const value = row[column] || '';
        // Escape quotes and wrap in quotes if contains comma or newline
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  const downloadCSV = () => {
    // Convert the filtered values to CSV format
    const csvContent = convertToCSV(filteredDevices, ['Schedule Name', 'Serial No', 'Device Name', 'Status', 'Last Heartbeat'], ['schedule_name', 'serial_no', 'device_name', 'status', 'last_heartbeat']);
    
    // Create a Blob with the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a download link
    const link = document.createElement('a');
    
    // Set link properties
    link.setAttribute('href', url);
    link.setAttribute('download', "DeviceList_"+businessName+".csv");
    link.style.display = 'none';
    
    // Add to document, trigger click, and remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return !isNaN(date)
      ? `${date.toISOString().split("T")[0]} ${
          date.toTimeString().split(" ")[0]
        }`
      : "N/A";
  };
  let content;
  if (loading) {
    content = (
      <tr className={styles.bodyTr}>
        <td colSpan="5" className={styles.spinner}>
          <div className={styles.loader}></div>
        </td>
      </tr>
    );
  } else if (devices.length === 0) {
    content = (
      <tr className={styles.bodyTr}>
        <td colSpan="5">No registered devices</td>
      </tr>
    );
  } else {
    content = filteredDevices.map((device, index) => (
      <tr
        className={styles.bodyTr}
        key={device.id}
        onClick={() => handleRowClick(device.id, device.schedule_id)}
        data-schedule-id={device.schedule_id}
      >
        <td>{device.schedule_name}</td>
        <td>{device.serial_no}</td>
        <td>{device.device_name}</td>
        <td>
          <div>
            <div className={getStatusColor(device?.status)}>
              {device?.status}
            </div>
          </div>
        </td>
        <td>
          {device?.last_heartbeat ? formatDate(device.last_heartbeat) : "N/A"}
        </td>
      </tr>
    ));
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Device Monitoring | LIFELENZ</title>
      </Helmet>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <span className={styles.headerSpan}>
            Time Clock | All Devices
          </span>
          <span>
            <span>{pendingCount > 0 ? pendingCount : 'No'} offline punch{pendingCount !== 1 ? 'es' : ''} pending</span>
            <FontAwesomeIcon
                icon={faDownload}
                alt="download CSV"
                className={[styles.csvButton, loading && styles.csvButtonLoading].join(" ")}
                onClick={downloadCSV}
                data-tooltip-id="download-tooltip"
            />
            <Tooltip id="download-tooltip" content="Download CSV" place="left" />
            <FontAwesomeIcon
                icon={faRefresh}
                alt="refresh"
                className={[styles.fetchButton, loading && styles.fetchButtonLoading].join(" ")}
                onClick={fetchData}
                data-tooltip-id="refresh-tooltip"
            />
            <Tooltip id="refresh-tooltip" content="Refresh Data" place="left"/>
          </span>
        </div>
      </div>
      <main className={styles.mainTable}>
        <section className={styles.tableBody}>
          <table className={styles.table}>
            <colgroup>
              <col style={{width: "30%"}}></col>
              <col style={{width: "10%"}}></col>
              <col style={{width: "20%"}}></col>
              <col style={{width: "20%"}}></col>
              <col style={{width: "20%"}}></col>
            </colgroup>
            <thead className={styles.tHead}>
              <tr>
                <th>
                  <div className={styles.scheduleHeader}>
                  <div className={styles.scheduleName}>
                    Schedule
                  </div>
                  <div className={styles.scheduleFilter}>
                    <input
                      type="text"
                      id="scheduletypeahead"
                      value={filterText}
                      onChange={(e) => searchBoxChanged(e.target.value)}
                      className={styles.filterInput}
                      maxLength={200}
                      placeholder="Search"
                    />
                  </div>
                  </div>
                </th>
                <th>Device Serial No</th>
                <th>Device Name</th>
                <th>
                  Status
                  <label className={styles.statusCheckbox}>
                    <input type="checkbox" checked={activeChecked} onChange={activecheckBoxChanged}/>
                    Active ({counts.active})
                  </label>
                  <label className={styles.statusCheckbox}>
                    <input type="checkbox" checked={inactiveChecked} onChange={inactivecheckBoxChanged}/>
                    Inactive ({counts.inactive})
                  </label>
                  <label className={styles.statusCheckbox}>
                    <input type="checkbox" checked={neverActiveChecked} onChange={neverActivecheckBoxChanged}/>
                    N/A ({counts.neverActive})
                  </label>
                </th>
                <th>Last Access</th>
              </tr>
            </thead>
            <tbody className={styles.tBody}>{content}</tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
